import { useId, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, PriceInput, Select, Textarea, formatPrice } from '@artosera/ui';
import {
  DEVISES,
  ETATS_OEUVRE_CONFIEE,
  formatDate,
  type Devise,
  type OeuvreConfieeReponse,
  type PropositionDepot,
} from '@artosera/shared';
import { useShellContext } from '../shell/context.js';
import { galerieALaction } from '../../auth/sujet.js';
import { deposantApi, useDepots, useGesteDeposant } from '../../api/espaceDeposant.js';
import type { ContactPresente } from '../../api/contacts.js';
import { RechercheContact } from '../ventes/RechercheContact.js';
import { RechercheOeuvre } from '../ventes/RechercheOeuvre.js';
import { Field } from '../Field.js';
import styles from './DepotsRecus.module.css';

export function DepotsRecus({ contactId, oeuvreId }: { contactId?: string; oeuvreId?: string }) {
  const { galerie, galerieSlug } = useShellContext(),
    { t, i18n } = useTranslation(['regie', 'commun']);
  const [etat, setEtat] = useState(''),
    [retard, setRetard] = useState(false),
    [contact, setContact] = useState<ContactPresente | null>(null);
  const [creation, setCreation] = useState<{ proposition?: PropositionDepot } | null>(null);
  const [geste, setGeste] = useState<{
    ligne: OeuvreConfieeReponse;
    action: 'rendue' | 'reglement' | 'annuler-reglement';
  } | null>(null);
  const filtres = {
    ...(contactId ? { deposantId: contactId } : {}),
    ...(contact ? { deposantId: contact.id } : {}),
    ...(oeuvreId ? { oeuvreId } : {}),
    etat,
    enRetard: retard,
  };
  const liste = useDepots(galerieSlug, filtres),
    ecriture = galerieALaction(galerie, 'movements:write'),
    direction = galerieALaction(galerie, 'consignments:regler');
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const date = (s: string) => formatDate(new Date(s), langue, 'court');
  async function ouvrirGeste(
    ligne: OeuvreConfieeReponse,
    action: 'rendue' | 'reglement' | 'annuler-reglement',
  ) {
    const frais = await liste.refetch();
    const actuelle = frais.data?.oeuvres.find((l) => l.id === ligne.id);
    if (actuelle) setGeste({ ligne: actuelle, action });
  }
  return (
    <section className={styles.page} data-ui="page">
      <header>
        <h2>{t('deposant.titre')}</h2>
        {ecriture && <Button onClick={() => setCreation({})}>{t('deposant.ajouter')}</Button>}
      </header>
      {!contactId && (
        <RechercheContact
          galerieSlug={galerieSlug}
          contact={contact}
          onChoisir={setContact}
          onEffacer={() => setContact(null)}
        />
      )}
      <label>
        {t('deposant.etat')}
        <Select
          value={etat || 'tous'}
          onValueChange={(v) => setEtat(v === 'tous' ? '' : v)}
          options={[
            { value: 'tous', label: t('deposant.tous') },
            ...ETATS_OEUVRE_CONFIEE.map((e) => ({ value: e, label: t(`deposant.etats.${e}`) })),
          ]}
        />
      </label>
      <label>
        <input type="checkbox" checked={retard} onChange={(e) => setRetard(e.target.checked)} />{' '}
        {t('deposant.retard')}
      </label>
      {liste.isPending && <p role="status">{t('commun:chargement')}</p>}
      {liste.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
      {liste.data && (
        <>
          {liste.data.totaux.map((total) => (
            <p key={total.devise}>
              {total.devise} · {t('deposant.net')} :{' '}
              {formatPrice(total.netCentimes, langue, total.devise)} · {t('deposant.du')} :{' '}
              {formatPrice(total.duCentimes, langue, total.devise)} · {t('deposant.regle')} :{' '}
              {formatPrice(total.regleCentimes, langue, total.devise)}
            </p>
          ))}
          {!liste.data.oeuvres.length && <p>{t('deposant.vide')}</p>}
          <ul className={styles.liste}>
            {liste.data.oeuvres.map((l) => (
              <li key={l.id}>
                <h3>
                  <Link to={`/${galerieSlug}/oeuvres/${l.oeuvreId}`}>{l.titre}</Link>
                </h3>
                <p>
                  {t('deposant.confieePar')}{' '}
                  <Link to={`/${galerieSlug}/contacts/${l.deposantId}`}>{l.deposantNom}</Link> ·{' '}
                  {t(`deposant.etats.${l.etat}`)}
                </p>
                <p>
                  {t('deposant.net')} : {formatPrice(l.prixNetCentimes, langue, l.devise)} ·{' '}
                  {t('deposant.recue')} : {date(l.recueLe)}
                </p>
                {l.finPrevueLe && (
                  <p>
                    {t('deposant.echeance')} : {date(l.finPrevueLe)}
                  </p>
                )}
                {l.vendueLe && (
                  <p>
                    {t('deposant.vendueLe')} : {date(l.vendueLe)}
                  </p>
                )}
                {l.rendueLe && (
                  <p>
                    {t('deposant.rendueLe')} : {date(l.rendueLe)}
                  </p>
                )}
                {l.reglement && (
                  <p>
                    {t('deposant.regle')} :{' '}
                    {formatPrice(l.reglement.montantCentimes, langue, l.devise)} ·{' '}
                    {date(l.reglement.regleLe)} · {l.reglement.reference}
                  </p>
                )}
                {l.venteAnnulee && <p role="alert">{t('deposant.venteAnnulee')}</p>}
                {l.note && <p>{l.note}</p>}
                <div className={styles.actions}>
                  {l.etat === 'confiee' && ecriture && (
                    <Button variant="secondary" onClick={() => void ouvrirGeste(l, 'rendue')}>
                      {t('deposant.rendre')}
                    </Button>
                  )}
                  {l.etat === 'vendue' && !l.reglement && direction && (
                    <Button onClick={() => void ouvrirGeste(l, 'reglement')}>
                      {t('deposant.regler')}
                    </Button>
                  )}
                  {l.reglement && direction && (
                    <Button
                      variant="secondary"
                      onClick={() => void ouvrirGeste(l, 'annuler-reglement')}
                    >
                      {t('deposant.annuler')}
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {liste.data.propositions.length > 0 && (
            <section>
              <h3>{t('deposant.propositions')}</h3>
              <ul>
                {liste.data.propositions.map((p) => (
                  <li key={`${p.mouvementId}:${p.oeuvreId}`}>
                    {p.titre} · {p.deposantNom}{' '}
                    {ecriture && (
                      <Button variant="secondary" onClick={() => setCreation({ proposition: p })}>
                        {t('deposant.confirmer')}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
      {creation && (
        <CreationDepot
          contactId={contactId}
          oeuvreId={oeuvreId}
          proposition={creation.proposition}
          fermer={() => setCreation(null)}
        />
      )}
      {geste && (
        <GesteDepot ligne={geste.ligne} action={geste.action} fermer={() => setGeste(null)} />
      )}
    </section>
  );
}
function CreationDepot({
  contactId,
  oeuvreId,
  proposition,
  fermer,
}: {
  contactId?: string | undefined;
  oeuvreId?: string | undefined;
  proposition?: PropositionDepot | undefined;
  fermer: () => void;
}) {
  const { galerieSlug } = useShellContext(),
    { t, i18n } = useTranslation(['regie', 'commun']),
    prefixe = useId();
  const [contact, setContact] = useState<ContactPresente | null>(null),
    [oeuvre, setOeuvre] = useState<{ id: string; titre: string } | null>(null);
  const [prix, setPrix] = useState<number | null>(null),
    [devise, setDevise] = useState<Devise>('EUR'),
    [recue, setRecue] = useState(''),
    [fin, setFin] = useState(''),
    [note, setNote] = useState('');
  const creer = useGesteDeposant(galerieSlug, (corps: unknown, appel) =>
    deposantApi.creer(galerieSlug, corps, appel),
  );
  const deposantId = proposition?.deposantId ?? contactId ?? contact?.id,
    cibleId = proposition?.oeuvreId ?? oeuvreId ?? oeuvre?.id;
  return (
    <Dialog
      open
      title={t('deposant.ajouter')}
      closeLabel={t('commun:actions.fermer')}
      onOpenChange={(o) => {
        if (!o) fermer();
      }}
    >
      <form
        className={styles.formulaire}
        onSubmit={(e) => {
          e.preventDefault();
          creer.mutate(
            {
              deposantId,
              oeuvreId: cibleId,
              mouvementId: proposition?.mouvementId ?? null,
              ...(recue ? { recueLe: recue } : {}),
              finPrevueLe: fin || null,
              prixNetCentimes: prix,
              devise,
              note,
            },
            { onSuccess: fermer },
          );
        }}
      >
        {proposition && (
          <p>
            {proposition.titre} · {proposition.deposantNom}
          </p>
        )}
        {!contactId && !proposition && (
          <RechercheContact
            galerieSlug={galerieSlug}
            contact={contact}
            onChoisir={setContact}
            onEffacer={() => setContact(null)}
          />
        )}
        {!oeuvreId && !proposition && (
          <>
            <p>{oeuvre?.titre}</p>
            <RechercheOeuvre galerieSlug={galerieSlug} onChoisir={setOeuvre} />
          </>
        )}
        <Field htmlFor={`${prefixe}-prix`} label={t('deposant.net')}>
          <PriceInput
            id={`${prefixe}-prix`}
            lang={i18n.language === 'en' ? 'en' : 'fr'}
            currency={devise}
            cents={prix ?? 0}
            onCentsChange={setPrix}
          />
        </Field>
        <Field htmlFor={`${prefixe}-devise`} label={t('deposant.devise')}>
          <Select
            id={`${prefixe}-devise`}
            value={devise}
            onValueChange={(v) => setDevise(v as Devise)}
            options={DEVISES.map((d) => ({ value: d, label: d }))}
          />
        </Field>
        {!proposition && (
          <Field htmlFor={`${prefixe}-recue`} label={t('deposant.recue')}>
            <Input
              id={`${prefixe}-recue`}
              type="date"
              required
              value={recue}
              onChange={(e) => setRecue(e.target.value)}
            />
          </Field>
        )}
        <Field htmlFor={`${prefixe}-fin`} label={t('deposant.echeance')}>
          <Input
            id={`${prefixe}-fin`}
            type="date"
            value={fin}
            onChange={(e) => setFin(e.target.value)}
          />
        </Field>
        <Field htmlFor={`${prefixe}-note`} label={t('deposant.note')}>
          <Textarea
            id={`${prefixe}-note`}
            maxLength={1000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
        <Button
          type="submit"
          loading={creer.isPending}
          disabled={!deposantId || !cibleId || !prix || prix < 0}
        >
          {t('deposant.confirmer')}
        </Button>
        {creer.isError && <p role="alert">{t('deposant.erreur')}</p>}
      </form>
    </Dialog>
  );
}
function GesteDepot({
  ligne,
  action,
  fermer,
}: {
  ligne: OeuvreConfieeReponse;
  action: 'rendue' | 'reglement' | 'annuler-reglement';
  fermer: () => void;
}) {
  const { galerieSlug } = useShellContext(),
    { t, i18n } = useTranslation(['regie', 'commun']),
    prefixe = useId();
  const [prix, setPrix] = useState<number | null>(ligne.prixNetCentimes),
    [regleLe, setRegleLe] = useState(new Date().toISOString().slice(0, 10)),
    [reference, setReference] = useState(''),
    [note, setNote] = useState('');
  const geste = useGesteDeposant(galerieSlug, (corps: unknown, appel) =>
    deposantApi.geste(galerieSlug, ligne.id, action, corps, appel),
  );
  const titre = {
    rendue: t('deposant.rendre'),
    reglement: t('deposant.regler'),
    'annuler-reglement': t('deposant.annuler'),
  }[action];
  return (
    <Dialog
      open
      title={titre}
      closeLabel={t('commun:actions.fermer')}
      onOpenChange={(o) => {
        if (!o) fermer();
      }}
    >
      <form
        className={styles.formulaire}
        onSubmit={(e) => {
          e.preventDefault();
          let corps: object = { revision: ligne.revision };
          if (action === 'reglement')
            corps = { ...corps, regleLe, montantCentimes: prix, reference, note };
          if (action === 'annuler-reglement') corps = { ...corps, motif: note };
          geste.mutate(corps, { onSuccess: fermer });
        }}
      >
        <p>
          {ligne.titre} · {ligne.deposantNom}
        </p>
        {action === 'reglement' && (
          <>
            <Field htmlFor={`${prefixe}-date`} label={t('deposant.regleLe')}>
              <Input
                id={`${prefixe}-date`}
                type="date"
                required
                value={regleLe}
                onChange={(e) => setRegleLe(e.target.value)}
              />
            </Field>
            <Field htmlFor={`${prefixe}-prix`} label={t('deposant.montant')}>
              <PriceInput
                id={`${prefixe}-prix`}
                lang={i18n.language === 'en' ? 'en' : 'fr'}
                currency={ligne.devise}
                cents={prix ?? 0}
                onCentsChange={setPrix}
              />
            </Field>
            <Field htmlFor={`${prefixe}-ref`} label={t('deposant.reference')}>
              <Input
                id={`${prefixe}-ref`}
                maxLength={120}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </Field>
          </>
        )}
        {action !== 'rendue' && (
          <Field htmlFor={`${prefixe}-note`} label={t('deposant.motif')}>
            <Textarea
              id={`${prefixe}-note`}
              maxLength={1000}
              required={action === 'annuler-reglement' || prix !== ligne.prixNetCentimes}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
        )}
        <Button type="submit" loading={geste.isPending} disabled={geste.isError}>
          {t('commun:actions.confirmer')}
        </Button>
        {geste.isError && <p role="alert">{t('deposant.erreur')}</p>}
      </form>
    </Dialog>
  );
}
export function DepotsRecusPage() {
  const { t } = useTranslation('regie');
  return (
    <>
      <h1>{t('deposant.titre')}</h1>
      <DepotsRecus />
    </>
  );
}
